/**
 * @file This file contains the ScoreManager class, responsible for handling
 * loading, saving, and ranking high scores.
 * @module ScoreManager
 */

export class ScoreManager {
    /**
     * The key used to store high scores in localStorage.
     * @type {string}
     */
    static STORAGE_KEY = 'mergeVillageHighScores';
    /**
     * The maximum number of high scores to store.
     * @type {number}
     */
    static MAX_SCORES = 10;

    /**
     * Creates an instance of ScoreManager.
     */
    constructor() {
        /**
         * The array of high scores.
         * @private
         * @type {Array<{score: number, initials: string}>}
         */
        this.scores = this.loadScores();
    }

    /**
     * Loads high scores from localStorage.
     * @returns {Array<{score: number, initials: string}>} An array of loaded high scores, or an empty array if none are found.
     */
    loadScores() {
        const scoresJSON = localStorage.getItem(ScoreManager.STORAGE_KEY);
        return scoresJSON ? JSON.parse(scoresJSON) : [];
    }

    /**
     * Saves the current high scores to localStorage.
     */
    saveScores() {
        localStorage.setItem(ScoreManager.STORAGE_KEY, JSON.stringify(this.scores));
    }

    /**
     * Gets the current list of high scores.
     * @returns {Array<{score: number, initials: string}>} The array of high scores.
     */
    getScores() {
        return this.scores;
    }

    /**
     * Checks if a given score qualifies as a high score.
     * @param {number} score - The score to check.
     * @returns {boolean} True if the score is a high score, false otherwise.
     */
    isHighScore(score) {
        if (score <= 0) return false;
        const isListNotFull = this.scores.length < ScoreManager.MAX_SCORES;
        const isHigherThanLowest = this.scores.length > 0 && score > this.scores[this.scores.length - 1].score;
        return isListNotFull || isHigherThanLowest;
    }

    /**
     * Adds a new score to the high score list if it qualifies.
     * The list is sorted and truncated to `MAX_SCORES`.
     * @param {number} score - The score to add.
     * @param {string} initials - The initials associated with the score.
     */
    addScore(score, initials) {
        if (!this.isHighScore(score)) return;

        const newScore = { score, initials };
        this.scores.push(newScore);
        this.scores.sort((a, b) => b.score - a.score); // Sort descending
        this.scores = this.scores.slice(0, ScoreManager.MAX_SCORES); // Keep only top 10
        this.saveScores();
    }
}